/* =====================================================================
   BZT CNC-Rechner — Schnittdaten, Vorschub, Material-Eignung
   Reines Vanilla-JS, keine Abhängigkeiten, keine API-Calls.
   Wird von snippets/bzt-cnc-rechner.liquid gerendert.

   Formeln (Standard-Zerspanung):
     Drehzahl   n  = (vc * 1000) / (PI * D)        [U/min]
     Vorschub   vf = n * z * fz                    [mm/min]

   Materialwerte = seriöse Erfahrungs-Richtwerte (vc in m/min, fz in mm/Zahn
   für ~6 mm VHM-Fräser), konsistent mit den BZT-Wissenscenter-Tabellen.
   Werte sind RICHTWERTE — bewusst als Bereiche kommuniziert.
   Verifiziert 06/2026 (Workflow calc-values-verify).
   ===================================================================== */
(function () {
  'use strict';

  // Zweisprachig seit 23.09.2026 (vorher gab die EN-Seite deutsche Ergebnisse aus)
  var IS_EN = (document.documentElement.lang || '').toLowerCase().indexOf('en') === 0;
  var LOCALE = IS_EN ? 'en-GB' : 'de-DE';
  function t(de, en) { return IS_EN ? en : de; }
  var RPM = t('U/min', 'rpm');

  // vc [m/min], fzRef [mm/Zahn @ 6 mm], rpmCap = realistische Spindel-Obergrenze,
  // note = Material-spezifischer Hinweis fürs Ergebnis.
  var MATERIALS = {
    weichholz:  { label: 'Weichholz (Fichte, Kiefer)', label_en: 'Softwood (spruce, pine)',     vc: [300, 600], fz: 0.10, rpmCap: 24000, note: 'Scharfe Werkzeuge, hohe Vorschübe — sonst Ausrisse an der Oberkante.', note_en: 'Sharp tools, high feeds — otherwise tear-out at the top edge.' },
    hartholz:   { label: 'Hartholz (Eiche, Buche, Ahorn)', label_en: 'Hardwood (oak, beech, maple)', vc: [250, 500], fz: 0.08, rpmCap: 24000, note: 'Brandgefahr: lieber Vorschub erhöhen als Drehzahl senken.', note_en: 'Risk of burning: increase the feed rather than reducing the speed.' },
    mdf:        { label: 'MDF / Spanplatte', label_en: 'MDF / chipboard',               vc: [300, 600], fz: 0.09, rpmCap: 24000, note: 'Absaugung Pflicht — Feinstaub ist gesundheitsschädlich.', note_en: 'Dust extraction is mandatory — fine dust is harmful to health.' },
    multiplex:  { label: 'Multiplex / Sperrholz', label_en: 'Plywood',                       vc: [250, 500], fz: 0.08, rpmCap: 24000, note: 'Wechselnde Faserrichtung pro Schicht — Kompressionsfräser für saubere Kanten.', note_en: 'Grain direction changes per layer — use a compression cutter for clean edges.' },
    kunststoff: { label: 'Kunststoff (PMMA, POM, PVC)', label_en: 'Plastics (PMMA, POM, PVC)',    vc: [200, 500], fz: 0.07, rpmCap: 24000, note: 'Gefahr des Aufschmelzens — Späne müssen abtransportiert werden (Einzahnfräser).', note_en: 'Risk of melting — chips must be evacuated (single flute).' },
    aluminium:  { label: 'Aluminium (AlMg, AlCuMg)', label_en: 'Aluminium (AlMg, AlCuMg)',       vc: [150, 350], fz: 0.05, rpmCap: 24000, note: 'Schmierung/Sprühnebel nötig, sonst Aufbauschneiden. Steife Maschine erforderlich.', note_en: 'Lubrication / mist required, otherwise built-up edge. Rigid machine required.' },
    messing:    { label: 'Messing / Bronze', label_en: 'Brass / bronze',               vc: [100, 250], fz: 0.05, rpmCap: 24000, note: 'Gut zerspanbar, aber abrasiv — VHM-Werkzeuge bevorzugen.', note_en: 'Machines well but is abrasive — prefer solid carbide tools.' },
    stahl:      { label: 'Baustahl (S235, weich)', label_en: 'Mild steel (S235, soft)',         vc: [60, 120],  fz: 0.03, rpmCap: 18000, note: 'Nur mit sehr steifer Maschine, Kühlung Pflicht. Kleine Zustellung, geduldig.', note_en: 'Only with a very rigid machine, coolant mandatory. Small depth of cut, be patient.' }
  };

  // BZT-Baureihen: max. Vorschubgeschwindigkeit [mm/min] laut Baureihen-Specs
  // (bzt_series.specs_json, Stand 07/2026). Konservativ = kleinste Modellvariante.
  // PFB/PFI/PS haben keine veröffentlichte Angabe und fehlen deshalb bewusst.
  var MACHINES = {
    pf:     { label: 'BZT Serie PF', label_en: 'BZT PF Series',      vfMax: 4000,  url: '/collections/bzt-serie-pf' },
    pfa:    { label: 'BZT Serie PFA', label_en: 'BZT PFA Series',     vfMax: 30000, url: '/collections/bzt-serie-pfa' },
    pfe:    { label: 'BZT Serie PFE', label_en: 'BZT PFE Series',     vfMax: 8000,  url: '/collections/bzt-serie-pfe' },
    pfg_s:  { label: 'BZT Serie PFG-S', label_en: 'BZT PFG-S Series',   vfMax: 30000, url: '/collections/bzt-serie-pfg-s' },
    pfh:    { label: 'BZT Serie PFH', label_en: 'BZT PFH Series',     vfMax: 12000, url: '/collections/bzt-serie-pfh' },
    pfj:    { label: 'BZT Serie PFJ', label_en: 'BZT PFJ Series',     vfMax: 6000,  url: '/collections/bzt-serie-pfj' },
    pfk:    { label: 'BZT Serie PFK', label_en: 'BZT PFK Series',     vfMax: 8000,  url: '/collections/bzt-serie-pfk' },
    pfl:    { label: 'BZT Serie PFL', label_en: 'BZT PFL Series',     vfMax: 5000,  url: '/collections/bzt-serie-pfl' },
    pfr:    { label: 'BZT Serie PFR', label_en: 'BZT PFR Series',     vfMax: 25000, url: '/collections/bzt-serie-pfr' },
    pfs:    { label: 'BZT Serie PFS', label_en: 'BZT PFS Series',     vfMax: 25000, url: '/collections/bzt-serie-pfs' },
    pfu:    { label: 'BZT Serie PFU', label_en: 'BZT PFU Series',     vfMax: 8000,  url: '/collections/bzt-serie-pfu' },
    pfu_s:  { label: 'BZT Serie PFU-S', label_en: 'BZT PFU-S Series',   vfMax: 8000,  url: '/collections/bzt-serie-pfu-s' },
    pfu_s2: { label: 'BZT Serie PFU-S-2', label_en: 'BZT PFU-S-2 Series', vfMax: 8000,  url: '/collections/bzt-serie-pfu-s-2' },
    pfx:    { label: 'BZT Serie PFX', label_en: 'BZT PFX Series',     vfMax: 12000, url: '/collections/bzt-serie-pfx' }
  };

  // Baureihenname je Sprache (EN wie der Collection-Titel: „BZT PF Series“)
  function mlabel(m) { return IS_EN && m.label_en ? m.label_en : m.label; }

  // Zahnvorschub skaliert grob mit Werkzeugdurchmesser (kleine Fräser brechen sonst).
  function fzForDiameter(fzRef, D) {
    var f = Math.pow(D / 6, 0.55);            // 6 mm = Referenz
    var fz = fzRef * f;
    return Math.max(0.01, fz);
  }

  function round(v, step) { return Math.round(v / step) * step; }

  /* Feste 500er-Schritte liessen kleine Drehzahlen auf 0 zusammenfallen:
     Stahl Ø 80 ergibt 238,7 U/min → Math.round(238,7/500)*500 = 0.
     Betroffen war alles unter 250 U/min, also Stahl ab Ø 77 mm,
     Messing ab Ø 128 mm, Aluminium ab Ø 191 mm — praxisuebliche
     Messerkopf-Durchmesser. Zusaetzlich verzerrte die grobe Stufe die
     unteren Werte um bis zu 18 %. (Fix 26.08.2026)
     Schrittweite haengt jetzt vom Betrag ab. */
  function roundAdaptive(v) {
    if (!isFinite(v) || v <= 0) return 0;
    if (v < 100)   return Math.round(v);
    if (v < 1000)  return round(v, 10);
    if (v < 5000)  return round(v, 50);
    if (v < 20000) return round(v, 100);
    return round(v, 500);
  }
  function roundFeed(v) {
    if (!isFinite(v) || v <= 0) return 0;
    if (v < 100)  return Math.round(v);
    if (v < 1000) return round(v, 10);
    return round(v, 50);
  }

  function calc(materialKey, D, z, spindleMax, machineKey) {
    var m = MATERIALS[materialKey];
    if (!m || !(D > 0) || !(z > 0)) return null;
    var fz = fzForDiameter(m.fz, D);
    var machine = MACHINES[machineKey] || null;

    /* Material- und Spindelgrenze getrennt fuehren. Vorher wurden beide per
       Math.min verschmolzen — dadurch war die Auswahl „30.000 U/min" wirkungslos
       (Materialkappe 24.000 gewann immer) und der Hinweistext schob die
       Begrenzung faelschlich auf die Spindel. (Fix 26.08.2026) */
    var spindleCap  = spindleMax > 0 ? spindleMax : Infinity;
    var materialCap = m.rpmCap;
    var rpmCap = Math.min(materialCap, spindleCap);
    var nLowRaw  = (m.vc[0] * 1000) / (Math.PI * D);
    var nHighRaw = (m.vc[1] * 1000) / (Math.PI * D);
    var nLow  = Math.min(nLowRaw,  rpmCap);
    var nHigh = Math.min(nHighRaw, rpmCap);

    var vfLow  = nLow  * z * fz;
    var vfHigh = nHigh * z * fz;

    // Maschinen-Limit: Vorschub kappen und Drehzahl passend reduzieren,
    // damit der Zahnvorschub (Spandicke) konstant bleibt.
    var machineLimited = false;
    if (machine) {
      if (vfHigh > machine.vfMax) { vfHigh = machine.vfMax; nHigh = vfHigh / (z * fz); machineLimited = true; }
      if (vfLow  > machine.vfMax) { vfLow  = machine.vfMax; nLow  = vfLow  / (z * fz); }
    }

    /* Vorschub aus der ANGEZEIGTEN (gerundeten) Drehzahl ableiten — sonst
       ergibt vf = fz x z x n beim Nachrechnen nicht den angezeigten Wert. */
    var nLowR  = roundAdaptive(nLow);
    var nHighR = roundAdaptive(nHigh);
    var vfLowR  = roundFeed(nLowR  * z * fz);
    var vfHighR = roundFeed(nHighR * z * fz);
    if (machine) {
      if (vfHighR > machine.vfMax) vfHighR = roundFeed(machine.vfMax);
      if (vfLowR  > machine.vfMax) vfLowR  = roundFeed(machine.vfMax);
    }

    return {
      material: IS_EN && m.label_en ? m.label_en : m.label,
      note: IS_EN && m.note_en ? m.note_en : m.note,
      machine: machine,
      machineLimited: machineLimited,
      fz: fz,
      nLow:  nLowR,
      nHigh: nHighR,
      vfLow:  vfLowR,
      vfHigh: vfHighR,
      capped: nHighRaw > rpmCap,
      cappedBy: nHighRaw > rpmCap ? (materialCap <= spindleCap ? 'material' : 'spindel') : null,
      materialCap: materialCap,
      spindleCap: isFinite(spindleCap) ? spindleCap : null
    };
  }

  function fmt(n) { return n.toLocaleString(LOCALE); }
  function fx(n, d) { return n.toLocaleString(LOCALE, { minimumFractionDigits: d, maximumFractionDigits: d }); }
  // Bereich anzeigen; identische Enden zu einem Wert zusammenfassen ("24.000–24.000" → "24.000")
  function fmtRange(a, b) { return a === b ? fmt(a) : fmt(a) + '–' + fmt(b); }

  // Sprachpräfix für interne Links (/en/, /de-eu/ …). Vorher landeten englische
  // Besucher über Anfrage- und Baureihen-Links auf deutschen Seiten. (Fix 23.09.2026)
  function loc(path) {
    var r = (typeof window.BZT_ROOT === 'string') ? window.BZT_ROOT : ((location.pathname.match(/^\/(en|de-eu|en-eu)(?=\/|$)/) || [''])[0]);
    if (r === '' || path.charAt(0) !== '/' || path === r || path.indexOf(r + '/') === 0 || path.indexOf(r + '?') === 0 || path.indexOf(r + '#') === 0) return path;
    return r + path;
  }

  // CTA-Block nach der Berechnung: Link zur passenden Baureihe (falls vorhanden)
  // + Anfrage mit vorausgefüllter Nachricht (Werte aus der Berechnung).
  function buildCta(toolLabel, valuesText, machine) {
    var q = 'tool=' + encodeURIComponent(toolLabel) + '&values=' + encodeURIComponent(valuesText);
    if (machine) q += '&machine=' + encodeURIComponent(mlabel(machine));
    var machineBtn = machine
      ? '<a class="bzt-calc__cta-btn bzt-calc__cta-btn--ghost" href="' + loc(machine.url) + '" data-bzt-cta="tool-series" data-bzt-cta-source="tool-schnittdaten">' + t(machine.label + ' ansehen', 'View ' + mlabel(machine)) + '</a>'
      : '';
    return (
      '<div class="bzt-calc__cta">' +
        '<p class="bzt-calc__cta-text">' + (machine
          ? t('Passt zur <strong>' + machine.label + '</strong> — Fragen Sie unverbindlich an.', 'Suits the <strong>' + mlabel(machine) + '</strong> — enquire without obligation.')
          : t('Unsicher, welche Maschine passt? <strong>Wir beraten Sie gern.</strong>', 'Not sure which machine fits? <strong>We are happy to advise.</strong>')) + '</p>' +
        '<div class="bzt-calc__cta-actions">' + machineBtn +
          '<a class="bzt-calc__cta-btn bzt-calc__cta-btn--primary" href="' + loc('/pages/kontakt') + '?' + q + '" data-bzt-cta="tool-inquiry" data-bzt-cta-source="tool-schnittdaten">' + t('Anfrage mit diesen Werten', 'Enquire with these values') + '</a>' +
        '</div>' +
      '</div>'
    );
  }

  // Ergebnis als PNG exportieren: rendert die Ergebnis-Kacheln clientseitig
  // auf ein <canvas> (kein html2canvas o.ä. — reines Vanilla-JS).
  function buildExportBar(out, toolLabel, cells) {
    var bar = document.createElement('div');
    bar.className = 'bzt-calc__exportbar';
    bar.innerHTML =
      '<button type="button" class="bzt-calc__export-btn" data-bzt-export>' +
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 15V3M7 10l5 5 5-5M4 21h16"/></svg>' +
        t('Ergebnis als Bild speichern', 'Save result as image') +
      '</button>';
    out.appendChild(bar);
    bar.querySelector('[data-bzt-export]').addEventListener('click', function () {
      exportResultImage(toolLabel, cells);
    });
    // Berechnung teilen: Deep-Link der aktuellen Werte kopieren (23.09.2026)
    var share = document.createElement('button');
    share.type = 'button';
    share.className = 'bzt-calc__export-btn';
    share.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M10 13a5 5 0 0 0 7.07 0l3-3a5 5 0 0 0-7.07-7.07l-1 1M14 11a5 5 0 0 0-7.07 0l-3 3a5 5 0 0 0 7.07 7.07l1-1"/></svg><span>' + t('Link kopieren', 'Copy link') + '</span>';
    share.addEventListener('click', function () {
      copyText(location.href, function (ok) {
        var s = share.querySelector('span');
        s.textContent = ok ? t('✓ Link kopiert', '✓ Link copied') : t('Link in der Adresszeile', 'Use the address bar');
        setTimeout(function () { s.textContent = t('Link kopieren', 'Copy link'); }, 1600);
      });
    });
    bar.appendChild(share);
  }

  function exportResultImage(toolLabel, cells) {
    var W = 900, padX = 56, rowH = 108, headH = 150, footH = 90;
    var H = headH + cells.length * rowH + footH;
    var c = document.createElement('canvas');
    c.width = W * 2; c.height = H * 2; // Retina
    var ctx = c.getContext('2d');
    ctx.scale(2, 2);

    ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = '#1a365d'; ctx.fillRect(0, 0, W, 6);

    ctx.fillStyle = '#0a1828';
    ctx.font = '700 26px Inter, Arial, sans-serif';
    ctx.fillText('BZT ' + toolLabel, padX, 62);
    ctx.fillStyle = '#5a6b7b';
    ctx.font = '400 14px Inter, Arial, sans-serif';
    var today = new Date();
    var dateStr = today.toLocaleDateString(LOCALE);
    ctx.fillText('bzt-shop.de · ' + dateStr, padX, 86);

    var y = headH;
    cells.forEach(function (cell) {
      ctx.fillStyle = '#f4f6f9';
      roundRect(ctx, padX, y, W - padX * 2, rowH - 18, 12); ctx.fill();
      ctx.fillStyle = '#5f6f80';
      ctx.font = '700 12px Inter, Arial, sans-serif';
      ctx.fillText(cell.k.toUpperCase(), padX + 24, y + 34);
      ctx.fillStyle = '#0a1828';
      ctx.font = '800 28px Inter, Arial, sans-serif';
      ctx.fillText(cell.v, padX + 24, y + 68);
      y += rowH;
    });

    ctx.fillStyle = '#5a6b7b';
    ctx.font = '400 12px Inter, Arial, sans-serif';
    ctx.fillText(t('Unverbindlicher Richtwert. Details: ', 'Non-binding reference value. Details: ') + 'bzt-shop.de' + loc('/pages/tools'), padX, H - 40);

    c.toBlob(function (blob) {
      var a = document.createElement('a');
      var url = URL.createObjectURL(blob);
      a.href = url;
      a.download = 'bzt-' + toolLabel.toLowerCase().replace(/[^a-z0-9]+/g, '-') + '.png';
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      setTimeout(function () { URL.revokeObjectURL(url); }, 4000);
    });
  }
  function roundRect(ctx, x, y, w, h, r) {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.arcTo(x + w, y, x + w, y + h, r);
    ctx.arcTo(x + w, y + h, x, y + h, r);
    ctx.arcTo(x, y + h, x, y, r);
    ctx.arcTo(x, y, x + w, y, r);
    ctx.closePath();
  }

  // Count-Up-Animation: animiert alle Zahlen-Tokens in .bzt-calc__v von 0 auf Zielwert.
  var prefersReduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  function animateValues(root) {
    if (prefersReduced) return;
    var cells = root.querySelectorAll('.bzt-calc__v');
    cells.forEach(function (el) {
      var finalHTML = el.innerHTML;
      // Tokens: deutsche Zahlen (mit . als Tausender, , als Dezimal)
      var tokens = [];
      var re = IS_EN ? /\d[\d,]*(?:\.\d+)?/g : /\d[\d.]*(?:,\d+)?/g;
      var template = finalHTML.replace(re, function (m) {
        var clean = IS_EN ? m.replace(/,/g, '') : m.replace(/\./g, '').replace(',', '.');
        var val = parseFloat(clean);
        var sep = IS_EN ? '.' : ',';
        var decimals = (m.indexOf(sep) >= 0) ? (m.split(sep)[1] || '').length : 0;
        tokens.push({ val: val, decimals: decimals });
        return '\u0001'; // Sentinel (kollidiert nicht mit Inhalt)
      });
      if (!tokens.length) return;
      var start = null, dur = 420;
      function frame(ts) {
        if (start === null) start = ts;
        var p = Math.min(1, (ts - start) / dur);
        var eased = 1 - Math.pow(1 - p, 3); // easeOutCubic
        var i = 0;
        el.innerHTML = template.replace(/\u0001/g, function () {
          var t = tokens[i++];
          var cur = t.val * eased;
          return cur.toLocaleString(LOCALE, { minimumFractionDigits: t.decimals, maximumFractionDigits: t.decimals });
        });
        if (p < 1) requestAnimationFrame(frame);
        else el.innerHTML = finalHTML;
      }
      requestAnimationFrame(frame);
    });
  }

  // Clipboard-API fehlt ohne sicheren Kontext bzw. in manchen In-App-Browsern:
  // dann execCommand-Fallback, sonst sichtbare Rückmeldung statt Stille. (Fix 23.09.2026)
  function copyText(text, done) {
    function fallback() {
      var ta = document.createElement('textarea');
      ta.value = text; ta.setAttribute('readonly', '');
      ta.style.position = 'fixed'; ta.style.opacity = '0';
      document.body.appendChild(ta); ta.select();
      var ok = false;
      try { ok = document.execCommand('copy'); } catch (e) {}
      document.body.removeChild(ta);
      done(ok);
    }
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(text).then(function () { done(true); }, fallback);
    } else { fallback(); }
  }

  // Klick auf eine Ergebnis-Kachel kopiert den Wert (Wert steht in data-copy,
  // damit die Count-Up-Animation das Kopieren nicht verfälscht).
  function enhanceCells(out) {
    out.querySelectorAll('.bzt-calc__cell').forEach(function (cell) {
      var v = cell.querySelector('.bzt-calc__v');
      var k = cell.querySelector('.bzt-calc__k');
      if (!v || !k) return;
      var copyVal = v.textContent.replace(/\s+/g, ' ').trim();
      cell.setAttribute('data-copy', copyVal);
      cell.setAttribute('role', 'button');
      cell.setAttribute('tabindex', '0');
      cell.setAttribute('title', t('Klicken zum Kopieren', 'Click to copy'));
      cell.setAttribute('aria-label', k.textContent + ' ' + copyVal + t(' — klicken zum Kopieren', ' — click to copy'));
      function doCopy() {
        copyText(cell.getAttribute('data-copy'), function (ok) {
          var orig = k.innerHTML;
          cell.classList.add(ok ? 'bzt-calc__cell--copied' : 'bzt-calc__cell--copyfail');
          k.textContent = ok ? t('✓ Kopiert', '✓ Copied') : t('Markieren & kopieren', 'Select & copy');
          setTimeout(function () {
            cell.classList.remove('bzt-calc__cell--copied', 'bzt-calc__cell--copyfail');
            k.innerHTML = orig;
          }, 1400);
        });
      }
      cell.addEventListener('click', doCopy);
      cell.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); doCopy(); }
      });
    });
  }

  // Screenreader-Zusammenfassung: EIN Update pro Berechnung statt
  // aria-live auf dem animierten Ergebnis (Count-Up würde x-fach vorgelesen).
  function announce(root, text) {
    var live = root.querySelector('[data-bzt-calc-live]');
    if (!live) {
      live = document.createElement('span');
      live.setAttribute('data-bzt-calc-live', '');
      live.setAttribute('aria-live', 'polite');
      live.className = 'bzt-visually-hidden';
      root.appendChild(live);
    }
    live.textContent = text;
  }

  // Eigene Presets (Werkzeug-Kombis) je Tool in localStorage, max. 6 pro Tool.
  var PRESET_KEY = 'bzt_custom_presets_schnittdaten';
  function loadCustomPresets() {
    try { return JSON.parse(localStorage.getItem(PRESET_KEY)) || []; } catch (e) { return []; }
  }
  function saveCustomPresets(list) {
    try { localStorage.setItem(PRESET_KEY, JSON.stringify(list.slice(0, 6))); } catch (e) {}
  }

  function init(root) {
    var matSel = root.querySelector('[data-bzt-calc="material"]');
    var dInput = root.querySelector('[data-bzt-calc="diameter"]');
    var zInput = root.querySelector('[data-bzt-calc="teeth"]');
    var maSel  = root.querySelector('[data-bzt-calc="machine"]');
    var spSel  = root.querySelector('[data-bzt-calc="spindle"]');
    var out    = root.querySelector('[data-bzt-calc="result"]');
    var presets = root.querySelectorAll('[data-bzt-calc-preset]');
    var customHost = root.querySelector('[data-bzt-calc-custom-presets]');
    var saveBtn = root.querySelector('[data-bzt-calc-save-preset]');
    if (!matSel || !dInput || !zInput || !out) return;

    function renderCustomPresets() {
      if (!customHost) return;
      var list = loadCustomPresets();
      customHost.innerHTML = '';
      // Chip und Entfernen-Kreuz sind Geschwister-Buttons: vorher lag ein
      // role=button-Span IN einem <button> (ungültig, Screenreader). (Fix 23.09.2026)
      list.forEach(function (p, idx) {
        var label = 'Ø ' + p.d + ' · ' + p.z + (p.z == 1 ? t(' Schneide', ' flute') : t(' Schneiden', ' flutes'));
        var wrap = document.createElement('span');
        wrap.className = 'bzt-calc__chipwrap';
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'bzt-calc__chip bzt-calc__chip--custom';
        btn.setAttribute('data-bzt-calc-preset', '');
        btn.setAttribute('data-d', p.d);
        btn.setAttribute('data-z', p.z);
        btn.textContent = label;
        btn.addEventListener('click', function () {
          dInput.value = p.d;
          zInput.value = p.z;
          update(true);
        });
        var x = document.createElement('button');
        x.type = 'button';
        x.className = 'bzt-calc__chip-remove';
        x.setAttribute('aria-label', t('Schnellwahl ' + label + ' entfernen', 'Remove quick pick ' + label));
        x.textContent = '✕';
        x.addEventListener('click', function () {
          var list2 = loadCustomPresets();
          list2.splice(idx, 1);
          saveCustomPresets(list2);
          renderCustomPresets();
          syncPresets();
        });
        wrap.appendChild(btn); wrap.appendChild(x);
        customHost.appendChild(wrap);
      });
    }
    if (saveBtn) {
      saveBtn.addEventListener('click', function () {
        var d = dInput.value, z = zInput.value;
        if (!(parseFloat(d) > 0) || !(parseInt(z, 10) > 0)) return;
        var list = loadCustomPresets();
        var exists = list.some(function (p) { return p.d == d && p.z == z; });
        if (!exists) {
          list.unshift({ d: d, z: z });
          saveCustomPresets(list);
          renderCustomPresets();
          syncPresets();
        }
        // Rückmeldung (vorher kommentarlos, auch wenn schon vorhanden)
        var lbl = saveBtn.querySelector('[data-bzt-save-label]');
        if (lbl) {
          lbl.textContent = exists ? t('Schon gespeichert', 'Already saved') : t('✓ Gespeichert', '✓ Saved');
          setTimeout(function () { lbl.textContent = t('Speichern', 'Save'); }, 1400);
        }
      });
    }
    renderCustomPresets();

    // Material-Optionen füllen
    Object.keys(MATERIALS).forEach(function (k) {
      var o = document.createElement('option');
      o.value = k; o.textContent = IS_EN && MATERIALS[k].label_en ? MATERIALS[k].label_en : MATERIALS[k].label;
      matSel.appendChild(o);
    });
    matSel.value = 'aluminium';

    // Maschinen-Optionen füllen
    if (maSel) {
      Object.keys(MACHINES).forEach(function (k) {
        var o = document.createElement('option');
        o.value = k; o.textContent = mlabel(MACHINES[k]);
        maSel.appendChild(o);
      });
    }

    // Deep-Link: Parameter aus der URL übernehmen (?mat=…&d=…&z=…&ma=…&s=…)
    try {
      var q = new URLSearchParams(location.search);
      if (q.get('mat') && MATERIALS[q.get('mat')]) matSel.value = q.get('mat');
      var clampTo = function (el, raw) {
        var v = parseFloat(String(raw).replace(',', '.'));
        if (!(v > 0)) return;
        var mn = parseFloat(el.min), mx = parseFloat(el.max);
        if (isFinite(mn) && v < mn) v = mn;
        if (isFinite(mx) && v > mx) v = mx;
        el.value = String(el === zInput ? Math.round(v) : v);
      };
      if (q.get('d') !== null) clampTo(dInput, q.get('d'));
      if (q.get('z') !== null) clampTo(zInput, q.get('z'));
      if (maSel && q.get('ma') && MACHINES[q.get('ma')]) {
        maSel.value = q.get('ma');
        var adv = root.querySelector('.bzt-calc__adv');
        if (adv) adv.open = true;
      }
      if (spSel && q.get('s') && spSel.querySelector('option[value="' + q.get('s').replace(/[^0-9]/g, '') + '"]')) spSel.value = q.get('s').replace(/[^0-9]/g, '');
    } catch (e) {}

    // Cockpit (Relaunch 23.09.2026): Materialkarten, Stepper, Tacho, Ergebnisleiste
    var core = window.BZTTool;
    var gauge = null, bar = null;
    if (core && root.classList.contains('bzt-cockpit')) {
      var cards = root.querySelector('[data-bzt-calc-matcards]');
      if (cards) core.materialCards(matSel, cards, function (k) { return 'vc ' + MATERIALS[k].vc[0] + '–' + MATERIALS[k].vc[1] + ' m/min'; });
      core.steppers(root);
      var gHost = root.querySelector('[data-bzt-calc-gauge]');
      if (gHost) gauge = core.gauge(gHost);
      var panel = root.querySelector('[data-bzt-calc-panel]');
      if (panel) bar = core.resultBar(root, panel, t('Drehzahl', 'Speed'));
      var placeRec = core.attachRec(panel, out);
    }

    // Clarity-Event: pro Seitenaufruf einmal, nur bei echter Interaktion.
    var tracked = false;
    function track() {
      if (tracked || !window.clarity) return;
      tracked = true;
      try { window.clarity('event', 'tool_used_schnittdaten'); } catch (e) {}
    }

    function syncUrl() {
      if (!window.history || !window.history.replaceState) return;
      try {
        var q = new URLSearchParams(location.search);
        q.set('mat', matSel.value); q.set('d', dInput.value); q.set('z', zInput.value);
        if (maSel && maSel.value) q.set('ma', maSel.value); else q.delete('ma');
        if (spSel && spSel.value !== '24000') q.set('s', spSel.value); else q.delete('s');
        history.replaceState(null, '', location.pathname + '?' + q.toString() + location.hash);
      } catch (e) {}
    }

    function syncPresets() {
      root.querySelectorAll('[data-bzt-calc-preset]').forEach(function (btn) {
        var active = btn.getAttribute('data-d') === String(parseFloat(dInput.value.replace(',', '.'))) &&
                     btn.getAttribute('data-z') === zInput.value;
        btn.classList.toggle('bzt-calc__chip--active', active);
        btn.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
    }

    function update(fromUser) {
      var D = parseFloat(dInput.value.replace(',', '.'));
      var z = parseInt(zInput.value, 10);
      var spindleMax = spSel ? parseInt(spSel.value, 10) : 0;
      var dMax = parseFloat(dInput.max), zMax = parseInt(zInput.max, 10);
      if ((D > 0 && isFinite(dMax) && D > dMax) || (z > 0 && isFinite(zMax) && z > zMax)) {
        out.innerHTML = '<p class="bzt-calc__hint">' + t('Bitte Werkzeug-Ø bis ' + fmt(dMax) + ' mm und höchstens ' + zMax + ' Schneiden eingeben.', 'Please enter a tool Ø up to ' + fmt(dMax) + ' mm and at most ' + zMax + ' flutes.') + '</p>';
        if (bar) bar.set('–');
        return;
      }
      var r = calc(matSel.value, D, z, spindleMax, maSel ? maSel.value : '');
      if (!r) {
        out.innerHTML = '<p class="bzt-calc__hint">' + t('Bitte Durchmesser und Schneidenanzahl eingeben.', 'Please enter diameter and number of flutes.') + '</p>';
        if (bar) bar.set('–');
        return;
      }
      var vN = fmtRange(r.nLow, r.nHigh) + ' ' + RPM;
      var vVf = fmtRange(r.vfLow, r.vfHigh) + ' mm/min';
      var FZU = t('mm/Zahn', 'mm/tooth');
      var vFz = fx(r.fz, 3) + ' ' + FZU;

      out.innerHTML =
        '<div class="bzt-calc__grid">' +
          '<div class="bzt-calc__cell"><span class="bzt-calc__k">' + t('Drehzahl n', 'Speed n') + '</span>' +
            '<span class="bzt-calc__v">' + fmtRange(r.nLow, r.nHigh) + ' <small>' + RPM + '</small></span></div>' +
          '<div class="bzt-calc__cell"><span class="bzt-calc__k">' + t('Vorschub v<sub>f</sub>', 'Feed v<sub>f</sub>') + '</span>' +
            '<span class="bzt-calc__v">' + fmtRange(r.vfLow, r.vfHigh) + ' <small>mm/min</small></span></div>' +
          '<div class="bzt-calc__cell"><span class="bzt-calc__k">' + t('Zahnvorschub f<sub>z</sub>', 'Feed per tooth f<sub>z</sub>') + '</span>' +
            '<span class="bzt-calc__v">' + fx(r.fz, 3) + ' <small>' + FZU + '</small></span></div>' +
        '</div>' +
        '<p class="bzt-calc__copyhint">' + t('Tipp: Klick auf eine Kachel kopiert den Wert.', 'Tip: click a value to copy it.') + '</p>' +
        (r.capped && !r.machineLimited
          ? '<p class="bzt-calc__hint bzt-calc__hint--info"><strong>' + t('Hinweis:', 'Note:') + '</strong> ' + t('Die rechnerische Drehzahl wurde begrenzt — ', 'The calculated speed was limited — ')
            + (r.cappedBy === 'spindel'
                ? t('durch die gewählte Spindel-Obergrenze von ' + fmt(r.spindleCap) + ' U/min.', 'by the selected spindle maximum of ' + fmt(r.spindleCap) + ' rpm.')
                : t('durch den für ' + r.material + ' empfohlenen Höchstwert von ' + fmt(r.materialCap) + ' U/min. Eine schnellere Spindel ändert daran nichts.', 'by the maximum recommended for ' + r.material + ' of ' + fmt(r.materialCap) + ' rpm. A faster spindle does not change this.'))
            + '</p>'
          : '') +
        (r.machineLimited ? '<p class="bzt-calc__hint bzt-calc__hint--info"><strong>' + t('Maschinen-Limit:', 'Machine limit:') + '</strong> ' + t('Der Vorschub wurde auf die maximale Verfahrgeschwindigkeit der ', 'The feed was limited to the maximum traverse speed of the ') + '<a href="' + loc(r.machine.url) + '">' + mlabel(r.machine) + '</a> (' + fmt(r.machine.vfMax) + t(' mm/min, kleinste Modellvariante) begrenzt und die Drehzahl passend reduziert — so bleibt die Spandicke erhalten.', ' mm/min, smallest model variant) and the speed reduced accordingly, so the chip thickness stays constant.') + '</p>' : '') +
        '<p class="bzt-calc__note">' + r.note + '</p>' +
        buildCta(t('Schnittdaten-Rechner', 'Cutting data calculator'), t('Drehzahl ', 'Speed ') + vN + ' · ' + t('Vorschub ', 'Feed ') + vVf + ' · ' + t('Zahnvorschub ', 'Feed per tooth ') + vFz + ' · ' + r.material, r.machine);

      enhanceCells(out);
      animateValues(out);
      announce(root, t('Drehzahl ', 'Speed ') + vN + '. ' + t('Vorschub ', 'Feed ') + vVf + '. ' + t('Zahnvorschub ', 'Feed per tooth ') + vFz + '.');
      syncPresets();
      if (gauge) {
        var lim = Math.min(r.materialCap, r.spindleCap || Infinity);
        gauge.update({
          lo: r.nLow, hi: r.nHigh, limit: lim,
          bandLabel: t('Empfohlen ', 'Recommended ') + fmtRange(r.nLow, r.nHigh) + ' ' + RPM,
          limitLabel: (r.spindleCap && r.spindleCap <= r.materialCap ? t('Spindel max. ', 'Spindle max. ') : t('Materialgrenze ', 'Material limit ')) + fmt(lim) + ' ' + RPM
        });
      }
      if (bar) {
        bar.label(t('Drehzahl · Vorschub ', 'Speed · feed ') + fmtRange(r.vfLow, r.vfHigh) + ' mm/min');
        bar.set(fmtRange(r.nLow, r.nHigh) + ' <small>' + RPM + '</small>');
      }
      // Werkzeugkette: Ergebnis direkt im nächsten Rechner weiterverwenden (23.09.2026)
      var vfMid = Math.round((r.vfLow + r.vfHigh) / 2 / 10) * 10;
      out.insertAdjacentHTML('beforeend',
        '<div class="bzt-chain"><span class="bzt-chain__k">' + t('Weiterrechnen mit v<sub>f</sub> ', 'Continue with v<sub>f</sub> ') + fmt(vfMid) + ' mm/min</span>' +
        '<a class="bzt-chain__a" data-bzt-cta="tool-chain" data-bzt-cta-source="tool-schnittdaten" href="' + loc('/pages/bearbeitungszeit-rechner') + '?vf=' + vfMid + '">' + t('Bearbeitungszeit', 'Machining time') + ' →</a>' +
        '<a class="bzt-chain__a" data-bzt-cta="tool-chain" data-bzt-cta-source="tool-schnittdaten" href="' + loc('/pages/zerspanvolumen-rechner') + '?vf=' + vfMid + '&ae=' + D + '">' + t('Zerspanvolumen', 'Material removal rate') + ' →</a></div>');
      if (placeRec) placeRec();
      buildExportBar(out, t('Schnittdaten-Rechner', 'Cutting data calculator'), [
        { k: t('Drehzahl n', 'Speed n'), v: vN },
        { k: t('Vorschub vf', 'Feed vf'), v: vVf },
        { k: t('Zahnvorschub fz', 'Feed per tooth fz'), v: vFz }
      ]);

      if (fromUser) { track(); syncUrl(); }
    }

    matSel.addEventListener('change', function () { update(true); });
    dInput.addEventListener('input', function () { update(true); });
    zInput.addEventListener('input', function () { update(true); });
    if (maSel) maSel.addEventListener('change', function () { update(true); });
    if (spSel) spSel.addEventListener('change', function () { update(true); });
    presets.forEach(function (btn) {
      btn.addEventListener('click', function () {
        dInput.value = btn.getAttribute('data-d');
        zInput.value = btn.getAttribute('data-z');
        update(true);
      });
    });
    update(false);
  }

  function boot() {
    document.querySelectorAll('[data-bzt-calc-root]').forEach(init);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
